/**
 * useStripeBilling Hook
 * 
 * Responsabilidade: Buscar e gerenciar dados reais de billing do Stripe.
 * Separação de Concerns: Apenas dados do Stripe, não lógica de UI.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { fetchBillingInfo, StripeBillingInfo } from '../../../services/modules/billing/billing.api';
import { createCustomerPortalSession } from '../../../services/modules/subscription/subscription.api';
import { notify } from '../../../utils/toast';

export interface UseStripeBillingReturn {
    billingInfo: StripeBillingInfo | null;
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    openCustomerPortal: () => Promise<void>;
    isPortalLoading: boolean;
    hasActiveSubscription: boolean;
}

export const useStripeBilling = (): UseStripeBillingReturn => {
    const { isAuthLoading, isAuthenticated } = useAuth();
    const { currentOrganization } = useOrganization();
    const [billingInfo, setBillingInfo] = useState<StripeBillingInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isPortalLoading, setPortalLoading] = useState(false);

    const fetchData = useCallback(async () => {
        if (isAuthLoading) {
            return;
        }

        if (!isAuthenticated || !currentOrganization?.id) {
            setBillingInfo(null);
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const data = await fetchBillingInfo(currentOrganization.id);
            setBillingInfo(data);
        } catch (err) {
            console.error('[useStripeBilling] Error:', err);
            // Não setar erro se for apenas "dados vazios"
            const message = err instanceof Error ? err.message : 'Erro ao carregar dados';
            if (!message.includes('Nenhuma assinatura')) {
                setError(message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [currentOrganization?.id, isAuthLoading, isAuthenticated]);

    useEffect(() => {
        fetchData();
    }, [
        fetchData,
        isAuthLoading,
        isAuthenticated,
        currentOrganization?.plan,
        currentOrganization?.subscriptionStatus,
        currentOrganization?.status,
    ]);

    useEffect(() => {
        const onBillingUpdated = () => fetchData();
        window.addEventListener('crm:billing-updated', onBillingUpdated);
        return () => window.removeEventListener('crm:billing-updated', onBillingUpdated);
    }, [fetchData]);

    const openCustomerPortal = useCallback(async () => {
        if (!currentOrganization?.id) {
            notify.error('Organização não selecionada.');
            return;
        }
        try {
            setPortalLoading(true);
            const returnUrl = `${window.location.origin}/billing`;
            const { url } = await createCustomerPortalSession(currentOrganization.id, returnUrl);
            if (url) {
                window.location.href = url;
            }
        } catch (err) {
            console.error('[useStripeBilling] Portal error:', err);
            const message = err instanceof Error ? err.message : 'Erro ao abrir portal';
            // Mostrar toast ao invés de setar erro global
            notify.error(message);
        } finally {
            setPortalLoading(false);
        }
    }, [currentOrganization?.id]);

    // Verificar se tem assinatura ativa (pode cancelar)
    // Retorna true se há subscription no Stripe OU se há dados de subscription locais
    const subscription = billingInfo?.subscription;
    const hasActiveSubscription = Boolean(
        subscription && 
        subscription.status !== 'trialing' &&
        !subscription.isTrialing
    );

    return {
        billingInfo,
        isLoading,
        error,
        refetch: fetchData,
        openCustomerPortal,
        isPortalLoading,
        hasActiveSubscription,
    };
};
